import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Chip, CardMedia, IconButton, Tooltip } from "@mui/material";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { DataGridPro } from "@mui/x-data-grid-pro";
import fileBroken from "../../../../../../public/images/fileBroken.jpg";
import { useHistory } from "react-router-dom";
import { numberFormatter } from "../../../../../utility/numberFormatter";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import FontDownloadRoundedIcon from "@mui/icons-material/FontDownloadRounded";
import { fileExtension, fileTypeToColor } from "../utils/fileUtils";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import CheckIcon from "@mui/icons-material/Check";

// file icons import
import wordImg from "../../../../../../public/images/wordImg.png";
import excelImg from "../../../../../../public/images/excelImg.png";
import pdfImg from "../../../../../../public/images/pdfImg.png";
import pptImg from "../../../../../../public/images/pptImg.png";
import mpImg from "../../../../../../public/images/mpImg.png";
import csvImg from "../../../../../../public/images/csvImg.png";
import zipImg from "../../../../../../public/images/zipImg.png";
import numberImg from "../../../../../../public/images/numberImg.png";
import defaultImg from "../../../../../../public/images/defaultImg.png";
import jsIcon from "../../../../../../public/images/jsIcon.svg";
import htmlIcon from "../../../../../../public/images/htmlIcon.svg";
import cssIcon from "../../../../../../public/images/cssIcon.svg";
interface Props {
  files?: any;
  loading?: boolean;
}
const ActionColumn = ({
  params
}: any) => {
  const [isCopied, setIsCopied] = useState(false);
  const handleCopyClick = (data: string) => {
    navigator?.clipboard?.writeText(data).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    }).catch(err => {
      console.error(err);
    });
  };
  return <IconButton onClick={(evt: any) => {
    evt.stopPropagation();
    handleCopyClick(params.row.FullPath);
  }} data-sentry-element="IconButton" data-sentry-component="ActionColumn" data-sentry-source-file="InsightsTable.tsx">
      {isCopied ? <CheckIcon sx={{
      color: "grey.400"
    }} /> : <LinkRoundedIcon sx={{
      color: "grey.400"
    }} />}
    </IconButton>;
};
const FilenameColumn = ({
  params
}: any) => {
  const [isImageError, setIsImageError] = useState(false);
  const handleImageError = () => {
    setIsImageError(true);
  };
  const FilenameTooltip = () => {
    return <Box sx={{
      display: "flex",
      alignItems: "center",
      ml: 3
    }} data-sentry-element="Box" data-sentry-component="FilenameTooltip" data-sentry-source-file="InsightsTable.tsx">
        <Tooltip title={params.row.filename || params.row.FileName.slice(1)} data-sentry-element="Tooltip" data-sentry-source-file="InsightsTable.tsx">
          <Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="InsightsTable.tsx">
            {params.row.filename || params.row.FileName.slice(1)}
          </Typography>
        </Tooltip>
      </Box>;
  };
  switch (fileExtension(params.row.filename || params.row.FileName.slice(1))) {
    case "jpg":
    case "jpeg":
    case "avif":
    case "gif":
    case "webp":
    case "png":
    case "svg":
    case "ico":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          minWidth: "52px",
          width: "52px",
          overflow: "hidden",
          backgroundColor: "grey.100",
          position: "relative",
          backgroundSize: `25px 25px`,
          backgroundPosition: `0 0, 12.5px 0, 12.5px -12.5px, 0px 12.5px`,
          boxSizing: "border-box"
        }}>
            <CardMedia component="img" onError={handleImageError} data-src={params.row.thumbnail} image={isImageError ? fileBroken : params.row.thumbnail} loading="lazy" sx={{
            objectFit: "contain",
            overflow: "hidden",
            height: "100%",
            verticalAlign: "bottom"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "ots":
    case "xls":
    case "xlsx":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "green.100",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={excelImg} image={excelImg} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "pdf":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "blue.50",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={pdfImg} image={pdfImg} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "csv":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "green.100",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={csvImg} image={csvImg} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "docx":
    case "doc":
    case "rtf":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "blue.50",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={wordImg} image={wordImg} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "ppt":
    case "pptx":
    case "pptm":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "red.50",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={pptImg} image={pptImg} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "aac":
    case "aiff":
    case "mid":
    case "mp3":
    case "wav":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "purple.100",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={mpImg} image={mpImg} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "mp4":
    case "mov":
    case "avi":
    case "wmv":
    case "mkv":
    case "webm":
    case "flv":
    case "f4v":
    case "swf":
    case "avchd":
    case "html5":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "#000",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="video" controls={false} src={params.row.thumbnail} sx={{
            backgroundColor: "#000",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            py: 1
          }} />
            <PlayCircleIcon fontSize="small" sx={{
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            m: "auto",
            color: "#FFF",
            position: "absolute",
            textAlign: "center"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "iso":
    case "rar":
    case "tgz":
    case "zip":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "grey.100",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={zipImg} image={zipImg} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "ai":
    case "bmp":
    case "eps":
    case "psd":
    case "tiff":
    case "tif":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "grey.100",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={defaultImg} image={defaultImg} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "numbers":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "green.100",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={numberImg} image={numberImg} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "js":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "blue.50",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={jsIcon} image={jsIcon} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "css":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "blue.50",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={cssIcon} image={cssIcon} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "html":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "blue.50",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <CardMedia component="img" data-src={htmlIcon} image={htmlIcon} loading="lazy" sx={{
            m: "auto",
            justifyContent: "center",
            width: "24px",
            height: "24px"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "otf":
    case "ttf":
    case "woff":
    case "woff2":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "blue.50",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <FontDownloadRoundedIcon fontSize="small" sx={{
            color: "grey.600",
            m: "auto"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    case "No Extension":
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          backgroundColor: "blue.50",
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center"
        }}>
            <ReportGmailerrorredIcon fontSize="small" sx={{
            color: "red.600",
            m: "auto"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
    default:
      return <Box sx={{
        display: "flex"
      }}>
          <Box sx={{
          height: "52px",
          width: "52px",
          overflow: "hidden",
          backgroundColor: "grey.100",
          position: "relative",
          backgroundSize: `25px 25px`,
          backgroundPosition: `0 0, 12.5px 0, 12.5px -12.5px, 0px 12.5px`,
          boxSizing: "border-box"
        }}>
            <CardMedia component="img" onError={handleImageError} data-src={params.row.thumbnail} image={isImageError ? fileBroken : params.row.thumbnail} loading="lazy" sx={{
            objectFit: "contain",
            overflow: "hidden",
            height: "100%",
            verticalAlign: "bottom"
          }} />
          </Box>
          <FilenameTooltip />
        </Box>;
  }
};
export const InsightsTable: FC<Props> = ({
  files,
  loading
}) => {
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const columns = [{
    field: "filename",
    headerName: t("common.name"),
    flex: 1,
    renderCell: (params: any) => <FilenameColumn params={params} />
  }, {
    field: "Requests",
    headerName: t("media.insightsTableColRequests"),
    width: 140
  }, {
    field: "ThroughtputGB",
    headerName: t("media.insightsTableColBandwidth"),
    width: 140,
    renderCell: (params: any) => {
      return <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2">
              {numberFormatter.format(params.row.ThroughtputGB)} GB
            </Typography>
          </Box>;
    }
  }, {
    field: "type",
    headerName: t("media.insightsTableColType"),
    width: 88,
    sortable: false,
    renderCell: (params: any) => {
      return <Chip label={fileExtension(params.row.filename || params.row.FileName)} sx={{
        textTransform: "uppercase",
        backgroundColor: `${fileTypeToColor(fileExtension(params.row.filename || params.row.FileName))}.100`,
        color: `${fileTypeToColor(fileExtension(params.row.filename || params.row.FileName))}.600`
      }} size="small" />;
    }
  }, {
    field: "action",
    headerName: "",
    width: 72,
    sortable: false,
    renderCell: (params: any) => <ActionColumn params={params} />
  }];
  return <Box component="main" sx={{
    height: "100%",
    width: "100%",
    px: 4
  }} data-sentry-element="Box" data-sentry-component="InsightsTable" data-sentry-source-file="InsightsTable.tsx">
      {files && <AutoSizer>
          {({
        width,
        height
      }: Size) => <DataGridPro sx={{
        backgroundColor: "common.white",
        ".MuiDataGrid-row": {
          cursor: "pointer"
        },
        border: "none"
      }} style={{
        width: width,
        height
      }} columns={columns} rows={files} rowHeight={52} hideFooter disableColumnFilter disableColumnMenu loading={loading} onRowClick={(params: any) => {
        const locationParams = new URLSearchParams(location.search);
        locationParams.set("fileId", params.row.id);
        history.replace({
          pathname: location.pathname,
          search: locationParams.toString()
        });
      }} />}
        </AutoSizer>}
    </Box>;
};